import { useLocalAudioUpload } from "@/lib/hooks/slides/editor/useLocalAudioUpload";
import { useLocalVideoUpload } from "@/lib/hooks/slides/editor/useLocalVideoUpload";
import { useAppDispatch } from "@/store/hooks";
import { updateElementProps } from "@/store/slideEditorSlice";
import {
  IMAGE_SCALE_STEP,
  MAX_COLUMNS,
  MAX_ITEMS,
  MIN_COLUMNS,
  clampColumns,
  clampImageScale,
} from "@ai-teach/canvas-styles";
import type { OrderImageElement } from "@ai-teach/types";
import { FaMinus, FaMusic, FaPlus } from "react-icons/fa";
import { HiPlus, HiX } from "react-icons/hi";
import { MdVideoLibrary } from "react-icons/md";

interface OrderImageElementToolbarProps {
  orderImageElement: OrderImageElement;
}

const nullMedia = { src: null, id: null, filename: null };

function generateId() {
  return Math.random().toString(36).slice(2, 10);
}

/**
 * Authoring controls for the image ordering question: the optional audio/video prompt, how many
 * images the grid holds, and how that grid lays out. Tiles are never positioned or resized
 * individually — they are one uniform square size derived from the frame, the column count and
 * this scale, so the editor and the student's phone can't disagree about the layout.
 * The images themselves and the answer order are set on the tiles in the canvas.
 */
export function OrderImageElementToolbar({ orderImageElement }: OrderImageElementToolbarProps) {
  const dispatch = useAppDispatch();
  const { id, items, mediaType, media } = orderImageElement;

  const columns = clampColumns(orderImageElement.columns);
  const imageScale = clampImageScale(orderImageElement.imageScale);

  const setProps = (props: Partial<OrderImageElement>) =>
    dispatch(updateElementProps({ id, props }));

  const { handleFileDrop: handleAudioDrop, isUploading: isUploadingAudio } = useLocalAudioUpload(
    (uploaded) => setProps({ media: uploaded, mediaType: "audio" })
  );
  const { handleFileDrop: handleVideoDrop, isUploading: isUploadingVideo } = useLocalVideoUpload(
    (uploaded) => setProps({ media: uploaded, mediaType: "video" })
  );

  // Clamped here as well as in the style function, so repeated clicks can't drift the stored value
  // past the bounds and leave the displayed number disagreeing with what renders.
  const handleColumnsChange = (delta: number) =>
    setProps({ columns: clampColumns(columns + delta) });

  const handleImageScaleChange = (delta: number) =>
    setProps({ imageScale: clampImageScale(imageScale + delta) });

  const handleAddItem = () => {
    if (items.length >= MAX_ITEMS) return;
    setProps({
      items: [
        ...items,
        {
          id: generateId(),
          media: nullMedia,
          position: { x: 0, y: 0 },
          size: { width: 100, height: 100 },
          order: items.filter((it) => it.order !== undefined).length + 1,
        },
      ],
    });
  };

  const rows = Math.ceil(items.length / columns);
  const isUploading = isUploadingAudio || isUploadingVideo;

  return (
    <>
      {/* Optional audio/video prompt */}
      <div className="flex items-center gap-1">
        <span className="text-xs font-semibold text-gray-500 uppercase">Prompt</span>
        {(["audio", "video"] as const).map((type) => (
          <label
            key={type}
            title={
              type === "audio" ? "Upload audio prompt 上傳音檔" : "Upload video prompt 上傳影片"
            }
            className={`flex items-center gap-1 px-2 py-1 rounded-lg border text-xs font-semibold cursor-pointer transition-colors ${
              mediaType === type
                ? "bg-primary-500 border-primary-500 text-white"
                : "bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100"
            }`}
          >
            <input
              type="file"
              accept={type === "audio" ? "audio/*" : "video/*"}
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) (type === "audio" ? handleAudioDrop : handleVideoDrop)(file);
                e.target.value = "";
              }}
            />
            {type === "audio" ? (
              <FaMusic className="w-3 h-3" />
            ) : (
              <MdVideoLibrary className="w-3 h-3" />
            )}
            {type === "audio" ? "Audio" : "Video"}
          </label>
        ))}
        {isUploading && <span className="text-xs text-gray-400">Uploading...</span>}
        {mediaType && media?.src && (
          <button
            type="button"
            onClick={() => setProps({ mediaType: null, media: nullMedia })}
            aria-label="Remove prompt media"
            title="Remove prompt media 移除提示媒體"
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600"
          >
            <HiX className="w-3 h-3" />
          </button>
        )}
      </div>

      <div className="w-px h-6 bg-gray-300" />

      {/* Grid shape — rows follow from the image count, so only columns is authored */}
      <div className="flex items-center gap-1">
        <span className="text-xs font-semibold text-gray-500 uppercase">Columns</span>
        <button
          type="button"
          disabled={columns <= MIN_COLUMNS}
          onClick={() => handleColumnsChange(-1)}
          aria-label="Fewer columns"
          title="Fewer columns 減少欄數"
          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600 disabled:opacity-40"
        >
          <FaMinus className="w-3 h-3" />
        </button>
        <span className="text-xs font-medium text-gray-500 w-12 text-center select-none">
          {columns} × {rows}
        </span>
        <button
          type="button"
          disabled={columns >= MAX_COLUMNS}
          onClick={() => handleColumnsChange(1)}
          aria-label="More columns"
          title="More columns 增加欄數"
          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600 disabled:opacity-40"
        >
          <FaPlus className="w-3 h-3" />
        </button>
      </div>

      <div className="w-px h-6 bg-gray-300" />

      {/* Image size — every tile shares one square size; 100% fits the grid, above that it clips */}
      <div className="flex items-center gap-1">
        <span className="text-xs font-semibold text-gray-500 uppercase">Image size</span>
        <button
          type="button"
          onClick={() => handleImageScaleChange(-IMAGE_SCALE_STEP)}
          aria-label="Decrease image size"
          title="Decrease image size 縮小圖片"
          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600"
        >
          <FaMinus className="w-3 h-3" />
        </button>
        <span className="text-xs font-medium text-gray-500 w-10 text-center select-none">
          {Math.round(imageScale * 100)}%
        </span>
        <button
          type="button"
          onClick={() => handleImageScaleChange(IMAGE_SCALE_STEP)}
          aria-label="Increase image size"
          title="Increase image size 放大圖片"
          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600"
        >
          <FaPlus className="w-3 h-3" />
        </button>
      </div>

      <div className="w-px h-6 bg-gray-300" />

      {/* Image count */}
      <button
        type="button"
        onClick={handleAddItem}
        disabled={items.length >= MAX_ITEMS}
        title={
          items.length >= MAX_ITEMS
            ? `At most ${MAX_ITEMS} images 最多 ${MAX_ITEMS} 張圖片`
            : "Add image 新增圖片"
        }
        className="flex items-center gap-1 px-2 py-1 rounded-lg border border-gray-300 bg-gray-50 text-xs font-semibold text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <HiPlus className="w-3 h-3" />
        Add image
      </button>
    </>
  );
}
